import { Denominator, getSuffix, getValue } from './Denominator';

const DEFAULT_CURRENCY = '';
const DEFAULT_DENOMINATOR = Denominator.None;
const DEFAULT_DECIMALS = 0;
const DEFAULT_PERCENTAGE = false;
const DEFAULT_PERCENTAGE_SYMBOL = '%';
const DEFAULT_GROWTH = false;
const LARGE_POSITIVE = '>';
const LARGE_NEGATIVE = '<';
const DEFAULT_NO_POSTFIX = false;

const SCIENTIFIC_FORMAT_THRESHOLD = 100000000000;
const SCIENTIFIC_DECIMALS = 2;

export interface NumberFormatConfig {
  denominator?: Denominator;
  decimals?: number;
  percentage?: boolean;
  percentageSymbol?: string;
  growth?: boolean;
  currency?: string;
  originalValue?: number;
  noPostfix?: boolean;
  replaceZeroValueWith?: string;
  dynamicDenominator?: boolean;
}

export const DEFAULT_CONFIG: NumberFormatConfig = {
  currency: DEFAULT_CURRENCY,
  denominator: DEFAULT_DENOMINATOR,
  percentageSymbol: DEFAULT_PERCENTAGE_SYMBOL,
  percentage: DEFAULT_PERCENTAGE,
  growth: DEFAULT_GROWTH,
  decimals: DEFAULT_DECIMALS,
  noPostfix: DEFAULT_NO_POSTFIX,
};

export type NumberFormatter = (value: number, config: NumberFormatConfig) => string;

export function addTrailingZeroes(valueString: string, decimals: number): string {
  if (valueString.includes('.')) {
    const components = valueString.split('.');

    if (components.length > 2) {
      throw new Error('addTrailingZeroes: passed a string with more than one decimal point.');
    }
    const integerComponent = components[0];
    const decimalComponent = components[1];
    const decimalThings = [...Array(decimals - decimalComponent.length)].map(() => '0').join('');

    return `${integerComponent}.${decimalComponent}${decimalThings}`;
  }

  return `${valueString}.${[...Array(decimals)].map(() => '0').join('')}`;
}

export function formatAmount(value: number, config: NumberFormatConfig): string {
  const formattedNumber = formatNumber(value, config);

  if (config.replaceZeroValueWith && formattedNumber === 0) {
    return `${config.replaceZeroValueWith}`;
  }

  const prefixValue: string = prefix(value, config);
  const postfixValue: string = postfix(config);

  const decimals = config.decimals ?? 2;

  if (formattedNumber >= SCIENTIFIC_FORMAT_THRESHOLD) {
    return `${prefixValue}${scientificExpression(formattedNumber)}`;
  }

  let valueString = localeString(formattedNumber, decimals);

  if (needsTrailingZeroes(formattedNumber, decimals)) {
    valueString = `${addTrailingZeroes(valueString, decimals)}`;
  }

  const isNegativeCurrency = config.currency && valueString.startsWith('-');

  return `${isNegativeCurrency ? '-' : ''}${prefixValue}${
    isNegativeCurrency ? valueString.substring(1, valueString.length) : valueString
  }${postfixValue}`;
}

export function localeString(input: number, decimals: number): string {
  return input.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function needsTrailingZeroes(input: number, decimals: number): boolean {
  if (Number.isInteger(input)) {
    return decimals > 0;
  }
  const stringInput: string = input.toString();

  return stringInput.split('.')[1].length < decimals;
}

export function autoFormatAmount(value: number, config?: NumberFormatConfig): string {
  const autoConfig: NumberFormatConfig = autoGenerateFormatConfig(config);

  if (config && (config.denominator || config.denominator === '' || config.percentage)) {
    return formatAmount(value, autoConfig);
  }

  if (config?.currency) {
    const denominator = getLargestDenominator(value);

    const decimals = denominator === Denominator.None ? 2 : 1;

    return formatAmount(value, { ...autoConfig, denominator, decimals });
  }

  if (config?.dynamicDenominator) {
    const denominator = getLargestDenominator(value);

    const decimals = denominator === Denominator.None ? 0 : 1;

    return formatAmount(value, { ...autoConfig, denominator, decimals });
  }

  return formatAmount(value, autoConfig);
}

export function autoGenerateFormatConfig(config?: NumberFormatConfig): NumberFormatConfig {
  return { ...DEFAULT_CONFIG, ...config };
}

export function roundToDigits(value: number, radix: number | undefined): number {
  const radixValue = radix ?? 2;
  const factor = Math.pow(10, radixValue);

  return Math.round(value * factor) / factor;
}

export function getLargestDenominator(value: number): Denominator {
  const largestExponent = Math.floor(Math.log10(Math.abs(value)));

  if (largestExponent >= 12) {
    return Denominator.Trillions;
  }
  if (largestExponent >= 9) {
    return Denominator.Billions;
  }
  if (largestExponent >= 6) {
    return Denominator.Millions;
  }
  if (largestExponent >= 4) {
    return Denominator.Thousands;
  }

  return Denominator.None;
}

function formatNumber(value: number, config: NumberFormatConfig): number {
  if (value < -999 && config.percentage) {
    return -999;
  }

  if (value < 1000) {
    return roundToDigits(value / getValue(config.denominator ?? Denominator.None), config.decimals);
  }

  if (config.percentage) {
    return 999;
  }

  return roundToDigits(value / getValue(config.denominator ?? Denominator.None), config.decimals);
}

function scientificExpression(value: number) {
  return value.toExponential(SCIENTIFIC_DECIMALS);
}

export function postfix(config: NumberFormatConfig): string {
  if (config.noPostfix) {
    return '';
  }
  const postfixConfig = {
    percentage: false,
    percentageSymbol: '%',
    denominator: '',
  };

  postfixConfig.percentage = config.percentage || postfixConfig.percentage;
  postfixConfig.percentageSymbol = config.percentageSymbol ?? postfixConfig.percentageSymbol;
  postfixConfig.denominator = config.denominator ? getSuffix(config.denominator) : getSuffix(Denominator.None);

  return `${postfixConfig.denominator}${postfixConfig.percentage ? postfixConfig.percentageSymbol : ''}`;
}

export function prefix(value: number, config: NumberFormatConfig): string {
  const prefixConfig = {
    largeNumber: '',
    growth: '',
    currency: '',
  };

  if (config.percentage) {
    prefixConfig.largeNumber = value > 999 ? LARGE_POSITIVE : prefixConfig.largeNumber;
    prefixConfig.largeNumber = value < -999 ? LARGE_NEGATIVE : prefixConfig.largeNumber;
  }

  prefixConfig.growth = config.growth && value > 0 ? '+' : prefixConfig.growth;
  prefixConfig.currency = config.currency ?? prefixConfig.currency;

  return `${prefixConfig.largeNumber}${prefixConfig.growth}${prefixConfig.currency}`;
}
